import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Star, Phone, ArrowLeft, Check, Quote, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPreviewPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLanding = async () => {
      try {
        const response = await axios.get(`${API}/landing-pages/${businessId}`);
        setLanding(response.data);
      } catch (error) {
        console.error('Error fetching landing:', error);
        toast.error('Landing page nie istnieje');
        navigate(`/firma/${businessId}`);
      } finally {
        setLoading(false);
      }
    };
    fetchLanding();
  }, [businessId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!landing) return null;

  const content = landing.content || {};
  const business = landing.business_data || {};
  const services = content.services || [];
  const whyUs = content.why_us || [];

  return (
    <div className="min-h-screen bg-white landing-preview">
      {/* Admin Bar */}
      <div className="bg-slate-900 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/firma/${businessId}`)}
              className="text-white hover:text-amber-400"
              data-testid="back-to-business-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Powrót do profilu
            </Button>
            <Badge variant="outline" className="border-amber-500 text-amber-400">
              Podgląd Landing Page
            </Badge>
          </div>
          <Link to="/" data-testid="back-to-app-btn">
            <Button variant="ghost" size="sm" className="text-white hover:text-amber-400">
              <ExternalLink className="w-4 h-4 mr-2" />
              lokalnie.pro
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-slate-100 py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-amber-100 text-amber-700 px-4 py-1">
            {business.category}
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight font-display" data-testid="landing-headline">
            {content.headline || business.name}
          </h1>
          <p className="text-xl sm:text-2xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed">
            {content.subheadline || `Lokalna firma z ${business.city}`}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {business.phone && (
              <a href={`tel:${business.phone}`}>
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 h-14 text-lg" data-testid="landing-cta-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  {content.cta_text || 'Zadzwoń teraz'}
                </Button>
              </a>
            )}
            <div className="flex items-center gap-2 text-slate-500">
              <div className="flex">
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              </div>
              <span className="font-medium">{business.rating}</span>
              <span>({business.reviews_count} opinii)</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">O nas</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-6 font-display">
                {business.name}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                {content.about || `Zapraszamy do ${business.name} - Twojego zaufanego partnera w kategorii ${business.category}.`}
              </p>
              <div className="flex items-center gap-2 text-slate-500">
                <MapPin className="w-5 h-5" />
                <span>{business.address}, {business.city}</span>
              </div>
            </div>
            <div className="relative">
              {business.photos && business.photos[0] ? (
                <img
                  src={business.photos[0]}
                  alt={business.name}
                  className="rounded-2xl shadow-2xl"
                />
              ) : (
                <div className="aspect-square bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center">
                  <span className="text-6xl font-bold text-amber-500/50">{business.name?.charAt(0)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {services.length > 0 && (
        <section className="py-16 sm:py-24 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Oferta</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 font-display">
                Nasze usługi
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map(function(service, idx) {
                return (
                  <Card key={idx} className="h-full hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                        <Check className="w-6 h-6 text-amber-600" />
                      </div>
                      <h3 className="font-semibold text-lg text-slate-900">{service}</h3>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why Us Section */}
      {whyUs.length > 0 && (
        <section className="py-16 sm:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Dlaczego my</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 font-display">
                Powody, by nas wybrać
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {whyUs.map(function(reason, idx) {
                return (
                  <div key={idx} className="text-center">
                    <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold text-white">{idx + 1}</span>
                    </div>
                    <h3 className="font-semibold text-xl text-slate-900">{reason}</h3>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonial Section */}
      {content.testimonial && (
        <section className="py-16 sm:py-24 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Quote className="w-12 h-12 text-amber-500 mx-auto mb-6" />
            <blockquote className="text-2xl sm:text-3xl text-white font-medium leading-relaxed mb-6 font-display italic">
              &ldquo;{content.testimonial}&rdquo;
            </blockquote>
            <div className="flex justify-center">
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
            </div>
            <p className="text-slate-400 mt-2">Zadowolony klient</p>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Kontakt</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 font-display">
              Skontaktuj się z nami
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Adres</p>
                    <p className="text-slate-600">{business.address}</p>
                    <p className="text-slate-600">{business.city}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {business.phone && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Telefon</p>
                      <a href={`tel:${business.phone}`} className="text-amber-600 hover:underline text-lg">
                        {business.phone}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-600 font-medium">{business.name}</p>
            <p className="text-sm text-slate-500">
              Strona wygenerowana przez{' '}
              <Link to="/" className="text-amber-600 hover:underline">
                lokalnie.pro
              </Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
